import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import {
  Save, RotateCcw, Loader2, CheckCircle2, AlertCircle, Image, Plus, Pencil, Trash2, X, LayoutList, BarChart3,
} from 'lucide-react';
import MediaPicker from '../components/MediaPicker';
import PopupAnalyticsReport from '../components/PopupAnalyticsReport';
import { APP_ROUTES } from '../lib/appRoutes';
import { SITE_NAME } from '../lib/brand';

interface Popup {
  id: number;
  name: string;
  isActive: boolean;
  priority: number;
  targetPages: string[];
  excludePages: string[];
  targetType: 'combined' | 'include' | 'exclude';
  delaySeconds: number;
  productImage?: string;
  productName?: string;
  eyebrow?: string;
  tagline?: string;
  benefits?: string;
  benefitsRotating?: boolean;
  ctaText?: string;
  ctaHref?: string;
  price?: string;
  maxShowsPerSession: number;
  trackingId?: string;
  createdAt: number;
  updatedAt: number;
}

const DEFAULT_FORM: Omit<Popup, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  isActive: true,
  priority: 10,
  targetPages: [],
  excludePages: ['/checkout', '/cart', '/admin'],
  targetType: 'combined',
  delaySeconds: 10,
  productImage: '/logo.png',
  productName: '',
  eyebrow: '為你推薦',
  tagline: '',
  benefits: '',
  benefitsRotating: false,
  ctaText: '立即選購 →',
  ctaHref: '/store',
  price: '',
  maxShowsPerSession: 1,
  trackingId: '',
};

// 顯示頁面清單由 src/lib/appRoutes.tsx 自動派生：
// 有 label 的顧客頁路由即出現（:param 路由取其前綴、以 /* 標示），
// admin 與無 label 的路由一律排除——新增頁面不用再手動同步這裡。
function routeToPopupEntry(r: { path: string; label?: string }): { value: string; label: string } | null {
  if (!r.label || r.path.startsWith('/admin')) return null;
  const parts = r.path.split('/').filter(Boolean);
  const cut = parts.findIndex((p) => p.startsWith(':'));
  const base = (cut >= 0 ? parts.slice(0, cut) : parts).join('/');
  const value = base ? `/${base}` : '/';
  const wildcard = cut >= 0 && value !== '/' ? '/*' : '';
  return { value, label: `${r.label} ${value}${wildcard}` };
}

function getPredefinedPaths(): { value: string; label: string }[] {
  const entries = APP_ROUTES
    .map(routeToPopupEntry)
    .filter((e): e is { value: string; label: string } => e !== null);
  // 去重：同值保留第一個；被更短前綴覆蓋的路徑不重複列出（彈窗比對本是前綴匹配）
  const seen = new Set<string>();
  return entries.filter((e) => {
    if (seen.has(e.value)) return false;
    const covered = [...seen].some((v) => e.value.startsWith(v + '/'));
    seen.add(e.value);
    return !covered;
  });
}

function pathListToText(paths: string[]): string {
  return paths.join('\n');
}

function textToPathList(text: string): string[] {
  return text.split('\n').map((p) => p.trim()).filter(Boolean);
}

function mergePaths(predefined: string[], custom: string[]): string[] {
  const set = new Set([...predefined, ...custom]);
  return Array.from(set);
}

function splitPaths(paths: string[]): { predefined: string[]; custom: string[] } {
  const predefinedValues = new Set(getPredefinedPaths().map((p) => p.value));
  const predefined: string[] = [];
  const custom: string[] = [];
  paths.forEach((p) => {
    if (predefinedValues.has(p)) predefined.push(p);
    else custom.push(p);
  });
  return { predefined, custom };
}

const AdminPopup: React.FC = () => {
  const navigate = useNavigate();
  const [popups, setPopups] = useState<Popup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingPopup, setEditingPopup] = useState<Popup | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaField, setMediaField] = useState<keyof Popup | null>(null);
  const [activeTab, setActiveTab] = useState<'management' | 'analytics'>('management');

  const [form, setForm] = useState<Omit<Popup, 'id' | 'createdAt' | 'updatedAt'>>({ ...DEFAULT_FORM });
  const [targetPredefined, setTargetPredefined] = useState<string[]>([]);
  const [targetCustom, setTargetCustom] = useState('');
  const [excludePredefined, setExcludePredefined] = useState<string[]>([]);
  const [excludeCustom, setExcludeCustom] = useState('');

  const loadPopups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/popups');
      if (!res.ok) throw new Error('載入彈窗列表失敗');
      const data = await res.json();
      setPopups(data.data?.popups || data.popups || []);
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    loadPopups();
  }, [navigate, loadPopups]);

  const resetForm = () => {
    setForm({ ...DEFAULT_FORM });
    setTargetPredefined([]);
    setTargetCustom('');
    setExcludePredefined(['/checkout', '/cart', '/admin']);
    setExcludeCustom('');
  };

  const openCreate = () => {
    resetForm();
    setEditingPopup(null);
    setIsModalOpen(true);
  };

  const openEdit = (popup: Popup) => {
    setEditingPopup(popup);
    setForm({
      name: popup.name,
      isActive: popup.isActive,
      priority: popup.priority,
      targetPages: popup.targetPages,
      excludePages: popup.excludePages,
      targetType: popup.targetType,
      delaySeconds: popup.delaySeconds,
      productImage: popup.productImage,
      productName: popup.productName,
      eyebrow: popup.eyebrow,
      tagline: popup.tagline,
      benefits: popup.benefits,
      benefitsRotating: popup.benefitsRotating,
      ctaText: popup.ctaText,
      ctaHref: popup.ctaHref,
      price: popup.price,
      maxShowsPerSession: popup.maxShowsPerSession,
      trackingId: popup.trackingId,
    });
    const targetSplit = splitPaths(popup.targetPages);
    setTargetPredefined(targetSplit.predefined);
    setTargetCustom(pathListToText(targetSplit.custom));
    const excludeSplit = splitPaths(popup.excludePages);
    setExcludePredefined(excludeSplit.predefined);
    setExcludeCustom(pathListToText(excludeSplit.custom));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPopup(null);
    setShowMediaPicker(false);
    setMediaField(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);

    const payload = {
      ...form,
      targetPages: mergePaths(targetPredefined, textToPathList(targetCustom)),
      excludePages: mergePaths(excludePredefined, textToPathList(excludeCustom)),
    };

    try {
      const url = editingPopup
        ? `/api/admin/popups/${editingPopup.id}`
        : '/api/admin/popups';
      const method = editingPopup ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '儲存失敗');
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await loadPopups();
      closeModal();
    } catch (err: any) {
      setError(err.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (popup: Popup) => {
    if (!window.confirm(`確定要刪除「${popup.name}」嗎？此操作無法復原。`)) return;
    try {
      const res = await apiFetch(`/api/admin/popups/${popup.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('刪除失敗');
      await loadPopups();
    } catch (err: any) {
      setError(err.message || '刪除失敗');
    }
  };

  const toggleActive = async (popup: Popup) => {
    try {
      const res = await apiFetch(`/api/admin/popups/${popup.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...popup, isActive: !popup.isActive }),
      });
      if (!res.ok) throw new Error('更新狀態失敗');
      await loadPopups();
    } catch (err: any) {
      setError(err.message || '更新狀態失敗');
    }
  };

  const handleRestoreDefault = async () => {
    if (!window.confirm('確定要還原為預設彈窗嗎？目前所有自訂彈窗將會被刪除，並重建預設值。')) return;
    try {
      // Delete all existing popups
      for (const popup of popups) {
        await apiFetch(`/api/admin/popups/${popup.id}`, { method: 'DELETE' });
      }
      // Create default popup
      await apiFetch('/api/admin/popups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: '預設進站推薦彈窗',
          isActive: true,
          priority: 0,
          targetPages: [],
          excludePages: ['/checkout', '/cart', '/admin'],
          targetType: 'exclude',
          delaySeconds: 15,
          productImage: '/logo.png',
          productName: '',
          eyebrow: '為你推薦',
          tagline: SITE_NAME,
          benefits: '嚴選優質素材，用心製作\n多款人氣產品，總有一款合你心水\n即買即寄，輕鬆購物',
          ctaText: '立即選購 →',
          ctaHref: '/store',
          price: '',
          maxShowsPerSession: 1,
          trackingId: 'default',
        }),
      });
      await loadPopups();
    } catch (err: any) {
      setError(err.message || '還原失敗');
    }
  };

  const togglePredefined = (
    value: string,
    selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const updateForm = <K extends keyof Omit<Popup, 'id' | 'createdAt' | 'updatedAt'>>(
    field: K,
    value: Omit<Popup, 'id' | 'createdAt' | 'updatedAt'>[K]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <AdminLayout currentPage="popup">
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="popup">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">進站推薦彈窗</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              管理多個針對不同頁面的推薦彈窗，系統會按優先級自動匹配當前頁面
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRestoreDefault}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              <RotateCcw className="w-4 h-4" />
              還原預設
            </button>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              <Plus className="w-4 h-4" />
              新增彈窗
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
          <button
            onClick={() => setActiveTab('management')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === 'management'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <LayoutList className="w-4 h-4" />
            彈窗管理
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === 'analytics'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            成效報表
          </button>
        </div>

        {error && activeTab === 'management' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {activeTab === 'analytics' ? (
          <PopupAnalyticsReport popups={popups} />
        ) : popups.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">尚未設定任何彈窗</p>
            <button
              onClick={openCreate}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              建立第一個彈窗
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">名稱</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">目標頁面</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">優先級</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">延遲</th>
                  <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">狀態</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {popups.map((popup) => (
                  <tr key={popup.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{popup.name}</div>
                      {popup.trackingId ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400">ID: {popup.trackingId}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {popup.targetPages.length > 0
                        ? popup.targetPages.join(', ')
                        : popup.targetType === 'exclude'
                          ? '所有頁面（排除指定頁面）'
                          : '所有頁面'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{popup.priority}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{popup.delaySeconds}s</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(popup)}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition ${
                          popup.isActive
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {popup.isActive ? '啟用' : '停用'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(popup)}
                          className="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition"
                          title="編輯"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(popup)}
                          className="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition"
                          title="刪除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
              <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {editingPopup ? '編輯彈窗' : '新增彈窗'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* 基本資料 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      彈窗名稱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      追蹤 ID
                    </label>
                    <input
                      type="text"
                      value={form.trackingId}
                      onChange={(e) => updateForm('trackingId', e.target.value)}
                      placeholder="例如：offers-to-store"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">用於分析報表識別，建議使用英文與連字符</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      優先級
                    </label>
                    <input
                      type="number"
                      value={form.priority}
                      onChange={(e) => updateForm('priority', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">數字越大越優先</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      延遲秒數
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={form.delaySeconds}
                      onChange={(e) => updateForm('delaySeconds', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      每 Session 顯示次數
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={form.maxShowsPerSession}
                      onChange={(e) => updateForm('maxShowsPerSession', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="popup-isActive"
                    checked={form.isActive}
                    onChange={(e) => updateForm('isActive', e.target.checked)}
                    className="w-4 h-4 accent-red-600"
                  />
                  <label htmlFor="popup-isActive" className="text-sm text-gray-800 dark:text-white">
                    啟用此彈窗
                  </label>
                </div>

                {/* 目標邏輯 */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    顯示邏輯
                  </label>
                  <select
                    value={form.targetType}
                    onChange={(e) => updateForm('targetType', e.target.value as Popup['targetType'])}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                  >
                    <option value="combined">組合：指定目標頁面，並可再排除</option>
                    <option value="include">僅顯示於指定頁面</option>
                    <option value="exclude">顯示於所有頁面，除了排除</option>
                  </select>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    路徑為前綴比對（勾選 /checkout 即涵蓋 /checkout/confirm/*）；管理後台（/admin/*）一律不顯示彈窗，無需手動排除。
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      目標頁面（勾選顯示的頁面）
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
                      {getPredefinedPaths().map((p) => (
                        <label key={p.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={targetPredefined.includes(p.value)}
                            onChange={() => togglePredefined(p.value, targetPredefined, setTargetPredefined)}
                            className="w-4 h-4 accent-red-600"
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                    <textarea
                      value={targetCustom}
                      onChange={(e) => setTargetCustom(e.target.value)}
                      placeholder={'自訂路徑，每行一個\n例如：/special-offer'}
                      rows={3}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      排除頁面（不顯示的頁面）
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
                      {getPredefinedPaths().map((p) => (
                        <label key={p.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={excludePredefined.includes(p.value)}
                            onChange={() => togglePredefined(p.value, excludePredefined, setExcludePredefined)}
                            className="w-4 h-4 accent-red-600"
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                    <textarea
                      value={excludeCustom}
                      onChange={(e) => setExcludeCustom(e.target.value)}
                      placeholder="自訂路徑，每行一個"
                      rows={3}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm font-mono"
                    />
                  </div>
                </div>

                {/* 內容設定 */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      眉標
                    </label>
                    <input
                      type="text"
                      value={form.eyebrow}
                      onChange={(e) => updateForm('eyebrow', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      標題
                    </label>
                    <input
                      type="text"
                      value={form.tagline}
                      onChange={(e) => updateForm('tagline', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    產品名稱 / 副標
                  </label>
                  <input
                    type="text"
                    value={form.productName}
                    onChange={(e) => updateForm('productName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    產品圖片
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.productImage}
                      onChange={(e) => updateForm('productImage', e.target.value)}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => { setMediaField('productImage'); setShowMediaPicker(true); }}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-1"
                    >
                      <Image size={16} /> 從媒體庫挑選
                    </button>
                  </div>
                  {form.productImage && (
                    <div className="mt-2 relative w-32 h-24 border rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img src={form.productImage} alt="預覽" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    賣點（每行一個）
                  </label>
                  <textarea
                    value={form.benefits}
                    onChange={(e) => updateForm('benefits', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                  />
                  <div className="mt-2 flex items-start gap-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none pt-0.5">
                      <input
                        type="checkbox"
                        checked={!!form.benefitsRotating}
                        onChange={(e) => updateForm('benefitsRotating', e.target.checked)}
                        className="w-4 h-4 text-red-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">賣點輪播</span>
                    </label>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      賣點超過 4 行時，前台彈窗會以連續向上滾動方式輪播賣點（每次約見 3 行，滑鼠停留可暫停；目前 {form.benefits.split('\n').map((b) => b.trim()).filter(Boolean).length} 行；4 行或以下維持全部顯示）。
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      按鈕文字
                    </label>
                    <input
                      type="text"
                      value={form.ctaText}
                      onChange={(e) => updateForm('ctaText', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      按鈕連結
                    </label>
                    <input
                      type="text"
                      value={form.ctaHref}
                      onChange={(e) => updateForm('ctaHref', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      價格（可選）
                    </label>
                    <input
                      type="text"
                      value={form.price}
                      onChange={(e) => updateForm('price', e.target.value)}
                      placeholder="HK$68"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                  className="flex items-center gap-2 px-6 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : saved ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? '儲存中…' : saved ? '已儲存' : '儲存變更'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Media Picker */}
        {showMediaPicker && mediaField && (
          <MediaPicker
            selectedUrl={form[mediaField] || ''}
            onSelect={(url) => { updateForm(mediaField as any, url); setShowMediaPicker(false); setMediaField(null); }}
            onClose={() => { setShowMediaPicker(false); setMediaField(null); }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPopup;
